import { useState } from 'react';
import { PlayCircle } from 'lucide-react';
import { FIREWALL_REPLAY_MAX_SAMPLES } from '@nadoutong/contracts';
import type { FirewallReplaySample, ReplayFirewallRulesetResponse } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import {
  parseFirewallReplayCorpus,
  type FirewallReplayCorpusError,
} from '../lib/firewallReplayCorpus.ts';
import { FirewallReplayReport } from './FirewallReplayReport.tsx';
import { Button, FormField, Textarea } from '../ui/index.ts';

/**
 * Submit a corpus against one version and read back what changed (#1760).
 *
 * This is also the gate's only entry point on the console: activation refuses a
 * version no replay covers, so an operator who cannot get here cannot arm
 * anything. It sits next to the version being inspected rather than inside the
 * activation dialog on purpose — reading a diff of verdicts is the work, and
 * doing it under a confirm button invites clicking through it.
 *
 * The corpus never leaves as anything but samples: the endpoint stores counts
 * and a fingerprint, never a header or a body. Redaction is still the
 * submitter's job, and it has to be substitution rather than deletion — a rule
 * asking whether `x-api-key` EXISTS must still see one.
 */

const CORPUS_ROWS = 8;

function errorParams(error: FirewallReplayCorpusError): Record<string, string | number> {
  switch (error.kind) {
    case 'too_many':
      return { count: error.count, max: FIREWALL_REPLAY_MAX_SAMPLES };
    case 'not_object':
      return { index: error.index + 1 };
    case 'bad_header':
      return { index: error.index + 1, header: error.header };
    default:
      return {};
  }
}

export function FirewallReplayPanel({
  version,
  pending,
  report,
  errorMessage,
  onSubmit,
}: {
  /** The version being judged — the one the operator is inspecting. */
  readonly version: number;
  readonly pending: boolean;
  /** The newest report, or `null` when this session has not replayed yet. */
  readonly report: ReplayFirewallRulesetResponse | null;
  readonly errorMessage: string | null;
  readonly onSubmit: (samples: readonly FirewallReplaySample[]) => void;
}): JSX.Element {
  const t = useT();
  const [text, setText] = useState('');
  const [showError, setShowError] = useState(false);

  const trimmed = text.trim();
  const parsed = trimmed.length === 0 ? null : parseFirewallReplayCorpus(trimmed);
  const parseError = parsed !== null && !parsed.ok ? parsed.error : null;
  const dropped = parsed !== null && parsed.ok ? parsed.corpus.droppedContextKeys : [];

  function submit(): void {
    if (parsed === null || !parsed.ok) {
      setShowError(true);
      return;
    }
    setShowError(false);
    onSubmit(parsed.corpus.samples);
  }

  return (
    <div className="flex flex-col gap-4" data-testid="firewall-replay-panel">
      <p className="m-0 text-label text-paper-mid">
        {t('firewall_rulesets.replay.intro', { version })}
      </p>
      <FormField
        label={t('firewall_rulesets.replay.field')}
        fieldId="firewall-replay-corpus"
        hint={t('firewall_rulesets.replay.hint', { max: FIREWALL_REPLAY_MAX_SAMPLES })}
        error={showError && parseError !== null
          ? t(`firewall_rulesets.replay.error.${parseError.kind}`, errorParams(parseError))
          : null}
      >
        <Textarea
          id="firewall-replay-corpus"
          mono
          rows={CORPUS_ROWS}
          value={text}
          invalid={showError && parseError !== null}
          aria-describedby={showError && parseError !== null ? 'firewall-replay-corpus-error' : undefined}
          disabled={pending}
          onChange={(event) => {
            setText(event.target.value);
            setShowError(false);
          }}
          data-testid="firewall-replay-corpus"
        />
      </FormField>

      {dropped.length === 0 ? null : (
        <p className="m-0 text-label text-warn" data-testid="firewall-replay-context-dropped">
          {t('firewall_rulesets.replay.context_dropped', { keys: dropped.join(', ') })}
        </p>
      )}

      <div>
        <Button
          variant="secondary"
          onClick={submit}
          disabled={pending || trimmed.length === 0}
          data-testid="firewall-replay-submit"
        >
          <PlayCircle aria-hidden="true" className="h-4 w-4" />
          <span>{t('firewall_rulesets.replay.submit', { version })}</span>
        </Button>
      </div>

      {errorMessage === null ? null : (
        <p className="m-0 text-label text-bad" role="alert" data-testid="firewall-replay-error">
          {errorMessage}
        </p>
      )}

      {report === null ? (
        <p className="m-0 text-label text-paper-low" data-testid="firewall-replay-none">
          {t('firewall_rulesets.replay.none')}
        </p>
      ) : (
        <FirewallReplayReport report={report} />
      )}
    </div>
  );
}
